import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { knowledgeApi } from '../../services/knowledge';
import type { KnowledgeEntry, SemanticSearchResult } from '../../types/knowledge';
import type { PaginationParams } from '../../types/api';
import { ENTRY_TYPE_MAP } from '../../constants/statusMappings';
import { renderStatusTag } from '../../utils/statusHelpers';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const KnowledgeEntries: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<KnowledgeEntry[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [semanticSearchVisible, setSemanticSearchVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [semanticResults, setSemanticResults] = useState<KnowledgeEntry[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [form] = Form.useForm();

  const fetchData = useCallback(async (params?: PaginationParams) => {
    setLoading(true);
    try {
      const response = await knowledgeApi.getEntries({
        page: params?.page || pagination.current,
        page_size: params?.page_size || pagination.pageSize,
      });
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
      if (response.pagination) {
        setPagination({
          current: response.pagination.page,
          pageSize: response.pagination.page_size,
          total: response.pagination.total,
        });
      }
    } catch {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchData({
      page: newPagination.current,
      page_size: newPagination.pageSize,
    });
  };

  const handleCreate = () => {
    setEditingEntry(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: KnowledgeEntry) => {
    setEditingEntry(record);
    form.setFieldsValue({
      ...record,
      metadata: record.metadata ? JSON.stringify(record.metadata) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeApi.deleteEntry(id);
      message.success('删除成功');
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[KnowledgeEntries] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handleSubmit = async (values: { title: string; entry_type: string; content: string; metadata?: string }) => {
    try {
      let parsedMetadata: Record<string, unknown> | undefined;
      if (values.metadata) {
        try {
          parsedMetadata = typeof values.metadata === 'string'
            ? JSON.parse(values.metadata)
            : values.metadata;
        } catch {
          message.error('元数据格式错误，请输入有效的JSON');
          return;
        }
      }
      const submitData = {
        type: values.entry_type,
        title: values.title,
        content: values.content,
        metadata: parsedMetadata,
      };
      if (editingEntry) {
        await knowledgeApi.updateEntry(editingEntry.id, {
          title: submitData.title,
          content: submitData.content,
          metadata: submitData.metadata,
        });
        message.success('更新成功');
      } else {
        await knowledgeApi.createEntry(submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[KnowledgeEntries] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handleSemanticSearch = async () => {
    if (!semanticQuery.trim()) {
      message.warning('请输入搜索内容');
      return;
    }
    setSemanticLoading(true);
    try {
      const response = await knowledgeApi.semanticSearch({
        query: semanticQuery,
        page_size: 20,
      });
      const result = response.data as SemanticSearchResult;
      setSemanticResults(result.data || []);
      message.success(`找到 ${result.total || 0} 条相关结果`);
    } catch {
      // 错误已处理
    } finally {
      setSemanticLoading(false);
    }
  };

  const handleImport = () => {
    // 由于API需要特定格式的数据，导入功能需要用户按照格式提供JSON文件
    Modal.info({
      title: '导入知识条目',
      content: (
        <div>
          <p>请通过以下方式导入数据：</p>
          <p>1. 准备符合格式的JSON数据</p>
          <p>2. 使用API接口进行批量导入</p>
          <p style={{ marginTop: 16 }}>导入格式示例：</p>
          <pre style={{ background: COLORS.gray100, padding: 8, fontSize: 12 }}>
{JSON.stringify({ entries: [{ type: 'cooperation', title: '示例标题', content: '示例内容' }] }, null, 2)}
          </pre>
        </div>
      ),
    });
  };

  const handleExport = async () => {
    try {
      // 直接使用 fetch 进行下载，支持 blob 响应
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/knowledge/export', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `knowledge-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch {
      message.error('导出失败');
    }
  };

  const filteredData = data.filter((item) => {
    const matchKeyword = !searchKeyword || 
      item.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      item.content.toLowerCase().includes(searchKeyword.toLowerCase());
    const matchType = !filterType || item.entry_type === filterType;
    return matchKeyword && matchType;
  });

  const columns: ColumnsType<KnowledgeEntry> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'entry_type',
      key: 'entry_type',
      width: 120,
      render: (type: string) => renderStatusTag(type, ENTRY_TYPE_MAP),
    },
    {
      title: '内容摘要',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => (
        <Text ellipsis={{ tooltip: content }} style={{ maxWidth: 300 }}>
          {content}
        </Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: KnowledgeEntry) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此知识条目？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索和操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Input
                placeholder="搜索标题或内容"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: 250 }}
                allowClear
              />
              <Select
                placeholder="类型筛选"
                value={filterType || undefined}
                onChange={setFilterType}
                style={{ width: 150 }}
                allowClear
              >
                {Object.entries(ENTRY_TYPE_MAP).map(([key, val]) => (
                  <Option key={key} value={key}>{val.text}</Option>
                ))}
              </Select>
              <Button
                icon={<RobotOutlined />}
                onClick={() => setSemanticSearchVisible(true)}
              >
                语义搜索
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<UploadOutlined />} onClick={handleImport}>
                导入
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                导出
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                新建条目
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingEntry ? '编辑知识条目' : '新建知识条目'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入知识条目标题" />
          </Form.Item>
          <Form.Item
            name="entry_type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="选择类型">
              {Object.entries(ENTRY_TYPE_MAP).map(([key, val]) => (
                <Option key={key} value={key}>{val.text}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea rows={6} placeholder="请输入知识条目内容" />
          </Form.Item>
          <Form.Item name="metadata" label="元数据（JSON格式）">
            <TextArea rows={3} placeholder='{"key": "value"}' />
          </Form.Item>
        </Form>
      </Modal>

      {/* 语义搜索弹窗 */}
      <Modal
        title="语义搜索"
        open={semanticSearchVisible}
        onCancel={() => {
          setSemanticSearchVisible(false);
          setSemanticResults([]);
          setSemanticQuery('');
        }}
        footer={null}
        width={800}
      >
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            placeholder="输入自然语言查询，如：寻找与科技产品合作相关的知识"
            value={semanticQuery}
            onChange={(e) => setSemanticQuery(e.target.value)}
            onPressEnter={handleSemanticSearch}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={semanticLoading}
            onClick={handleSemanticSearch}
          >
            搜索
          </Button>
        </Space.Compact>
        <Table
          columns={[
            {
              title: '标题',
              dataIndex: 'title',
              key: 'title',
              width: 150,
            },
            {
              title: '类型',
              dataIndex: 'entry_type',
              key: 'entry_type',
              width: 100,
              render: (type: string) => renderStatusTag(type, ENTRY_TYPE_MAP),
            },
            {
              title: '内容',
              dataIndex: 'content',
              key: 'content',
              ellipsis: true,
            },
            {
              title: '相似度',
              dataIndex: 'similarity_score',
              key: 'similarity_score',
              width: 100,
              render: (score: number) => 
                score ? `${(score * 100).toFixed(1)}%` : '-',
            },
          ]}
          dataSource={semanticResults}
          rowKey="id"
          loading={semanticLoading}
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  );
};
